import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { API } from "@/lib/api";
import { BookOpen, Download, Send, MapPin } from "lucide-react";
import SendToDeviceDialog from "@/components/SendToDeviceDialog";
import SmartImage from "@/components/SmartImage";

export default function Library() {
  const [books, setBooks] = useState([]);
  const [progress, setProgress] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sendBook, setSendBook] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get("/library").then((r) => setBooks(r.data)).catch(() => {}),
      api.get("/reading-progress").then((r) => setProgress(r.data || [])).catch(() => {}),
    ]).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div data-testid="library-page" className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24">
      <div className="label-eyebrow text-sage mb-3">Your shelf</div>
      <h1 className="font-serif text-5xl sm:text-6xl tracking-tight">My library.</h1>
      <p className="text-muted_ink mt-4 max-w-xl">Every book you've bought from Folio, in one place. Yours forever.</p>

      {/* Continue reading */}
      {progress.length > 0 && (
        <div data-testid="continue-reading" className="mt-12 border-t border-edge pt-10">
          <div className="label-eyebrow text-terracotta flex items-center gap-2 mb-4">
            <MapPin className="w-3.5 h-3.5" /> Continue reading
          </div>
          <div className="flex gap-4 overflow-x-auto pb-3 -mx-2 px-2">
            {progress.slice(0, 6).map((p) => {
              const cover = p.cover_url || (p.cover_path ? `${API}/files/${p.cover_path}` : null);
              const pct = Math.min(100, Math.max(0, p.percent || 0));
              return (
                <Link
                  key={p.book_id}
                  to={`/read/${p.book_id}`}
                  data-testid={`continue-${p.book_id}`}
                  className="group flex-shrink-0 w-56 border border-edge bg-white overflow-hidden hover:shadow-md transition-shadow"
                >
                  <div className="aspect-[3/4] bg-linen overflow-hidden">
                    {cover && <SmartImage src={cover} alt="" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]" />}
                  </div>
                  <div className="p-3">
                    <div className="font-serif text-sm line-clamp-1 group-hover:text-terracotta transition-colors">{p.book_title}</div>
                    <div className="text-[11px] text-muted_ink italic line-clamp-1">by {p.book_author}</div>
                    <div className="mt-2 h-1 bg-linen">
                      <div className="h-full bg-terracotta" style={{ width: `${pct}%` }} />
                    </div>
                    <div className="mt-1 text-[11px] text-muted_ink flex justify-between">
                      <span>Page {p.page}{p.total_pages ? ` / ${p.total_pages}` : ""}</span>
                      {pct > 0 && <span>{pct}%</span>}
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      )}

      <div className="mt-14">
        {loading ? (
          <div className="text-muted_ink">Loading…</div>
        ) : books.length === 0 ? (
          <div className="border border-edge p-16 text-center">
            <div className="font-serif text-3xl italic mb-3">An empty shelf.</div>
            <p className="text-muted_ink mb-6">You haven't bought any books yet.</p>
            <Link to="/browse" className="btn-primary">Browse the shelf</Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {books.map((b) => {
              const cover = b.cover_url || (b.cover_path ? `${API}/files/${b.cover_path}` : null);
              return (
                <div key={b.id} data-testid={`library-book-${b.id}`} className="bg-white border border-edge p-5 flex gap-5">
                  <div className="w-24 aspect-[3/4] flex-shrink-0 overflow-hidden bg-linen book-card-cover">
                    {cover && <SmartImage src={cover} alt={b.title} className="w-full h-full object-cover" />}
                  </div>
                  <div className="flex-1 flex flex-col">
                    <div className="label-eyebrow text-sage">{b.category}</div>
                    <h3 className="font-serif text-xl mt-1 leading-tight">{b.title}</h3>
                    <div className="text-sm text-muted_ink mt-1">{b.author}</div>
                    <div className="mt-auto pt-4 flex gap-2 flex-wrap">
                      {b.pdf_path && (
                        <Link
                          data-testid={`read-${b.id}`}
                          to={`/read/${b.id}`}
                          className="inline-flex items-center gap-2 text-xs uppercase tracking-widest px-3 py-2 border border-ink bg-ink text-paper hover:bg-terracotta hover:border-terracotta transition-colors"
                        >
                          <BookOpen className="w-3 h-3" /> Read
                        </Link>
                      )}
                      {b.pdf_path && (
                        <a
                          data-testid={`download-${b.id}`}
                          href={`${API}/files/${b.pdf_path}`}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-2 text-xs uppercase tracking-widest px-3 py-2 border border-edge hover:bg-linen transition-colors"
                        >
                          <Download className="w-3 h-3" />
                        </a>
                      )}
                      {b.pdf_path && (
                        <button
                          data-testid={`send-device-${b.id}`}
                          onClick={() => setSendBook(b)}
                          className="inline-flex items-center gap-2 text-xs uppercase tracking-widest px-3 py-2 border border-edge hover:bg-linen transition-colors"
                          title="Send to Kindle/Kobo"
                        >
                          <Send className="w-3 h-3" />
                        </button>
                      )}
                      <Link to={`/book/${b.id}`} className="inline-flex items-center text-xs uppercase tracking-widest px-3 py-2 text-muted_ink hover:text-ink">
                        Details
                      </Link>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {sendBook && (
        <SendToDeviceDialog
          open={!!sendBook}
          onOpenChange={(v) => !v && setSendBook(null)}
          bookId={sendBook.id}
          bookTitle={sendBook.title}
        />
      )}
    </div>
  );
}
